import { useMemo } from 'react';
import Select from 'react-select';
import { VtDocumentCardI } from '..';
import info from '../../../../../../assets/images/icons/info.svg';
import { CustomStyle } from '../../../../../../components/CustomSelectStyle';
import FilterRadioButton from '../../../../../../components/FilterRadioButtons';
import { FilterRadioButtonsContainer } from '../../../../../../components/FilterRadioButtonsContainer';
import FormGroup from '../../../../../../components/FormGroup';
import OpacityAnimation from '../../../../../../components/OpacityAnimation';
import Textarea from '../../../../../../components/Textarea';
import { Container, Group } from '../../../styles';
import {
  VT_PURCHASE_PROVIDER_NOT_INFORMED,
  VT_PURCHASE_PROVIDER_SAME_AS_SOURCE,
  VT_PURCHASE_PROVIDER_SAME_AS_SOURCE_LABEL,
  VtProviderType,
} from '../../../types';

export default function VtDocumentCard({
  vtProvider,
  setVtProvider,
  vtProviderOptions,
  vtPurchaseProvider,
  setVtPurchaseProvider,
  hasCustomLogo,
  setHasCustomLogo,
  showTicketsAtNotOptimizedResults,
  setShowTicketsAtNotOptimizedResults,
  hasCustomTextAtLetter,
  handleHasCustomTextAtLetterChange,
  customTextAtLetter,
  handleCustomTextAtLetterChange,
  getErrorMessageByFieldName,
  canRequestVtItemRelation,
  setCanRequestVtItemRelation,
}: VtDocumentCardI) {
  const isVtCodeSourceSelected = !!vtProvider.value && vtProvider.value !== VT_PURCHASE_PROVIDER_NOT_INFORMED;

  const vtPurchaseProviderOptions = useMemo(() => {
    const providerOptions = vtProviderOptions.filter((option) => option.value !== null);

    return [
      { value: VT_PURCHASE_PROVIDER_NOT_INFORMED, label: 'Não informado' },
      { value: VT_PURCHASE_PROVIDER_SAME_AS_SOURCE, label: VT_PURCHASE_PROVIDER_SAME_AS_SOURCE_LABEL },
      ...providerOptions,
    ];
  }, [vtProviderOptions]);

  function handleVtProviderChange(selectedProvider: VtProviderType | null) {
    setVtProvider(selectedProvider!);

    if (!selectedProvider?.value || selectedProvider.value === VT_PURCHASE_PROVIDER_NOT_INFORMED) {
      setVtPurchaseProvider({ value: VT_PURCHASE_PROVIDER_NOT_INFORMED, label: 'Não informado' });
    }
  }

  return (
    <Container>
      <div className="card-title">
        Carta de VT
      </div>

      <Group>
        <div className="title">
          <div>
            Origem do código de item de VT (folha e carta)
            {' '}
            <img
              src={info}
              alt="info"
              title="Define qual tabela de códigos será usada na folha de pagamento e na carta de VT. Pode ser a tabela de itens do fornecedor de VT ou a tabela de itens própria do cliente."
            />
          </div>
        </div>
        <Select
          value={vtProvider}
          options={vtProviderOptions}
          onChange={(selectedProvider) => handleVtProviderChange(selectedProvider as VtProviderType)}
          placeholder="Selecione a origem do código"
          styles={CustomStyle}
          classNamePrefix="react-select"
          className="react-select-container"
        />
      </Group>

      {isVtCodeSourceSelected && (
        <OpacityAnimation>
          <Group>
            <div className="title">
              <div>
                Fornecedor de VT para compra dos itens (retorno para Web Consulta)
                {' '}
                <img
                  src={info}
                  alt="info"
                  title="Informe o fornecedor real dos itens de vale-transporte quando a origem acima for a tabela de itens do cliente. Se a origem já for do fornecedor, selecione 'Mesmo fornecedor da origem acima'."
                />
              </div>
            </div>
            <Select
              value={vtPurchaseProvider}
              options={vtPurchaseProviderOptions}
              onChange={(selectedProvider) => {
                setVtPurchaseProvider(selectedProvider as VtProviderType);
              }}
              placeholder="Selecione um fornecedor"
              styles={CustomStyle}
              classNamePrefix="react-select"
              className="react-select-container"
            />
          </Group>
        </OpacityAnimation>
      )}

      <Group>
        <div className="title">
          <div>
            Utiliza logomarca própria na carta?
            {' '}
            <img src={info} alt="info" title="Exibe a logomarca na carta de vale transporte própria da empresa" />
          </div>
        </div>
        <FilterRadioButtonsContainer>
          <FilterRadioButton selected={hasCustomLogo} onClick={() => setHasCustomLogo(true)}>
            Sim
          </FilterRadioButton>
          <FilterRadioButton selected={!hasCustomLogo} onClick={() => setHasCustomLogo(false)}>
            Não
          </FilterRadioButton>
        </FilterRadioButtonsContainer>
      </Group>

      <Group>
        <div className="title">
          <div>
            Exibir bilhetes quando o resultado estiver sem otimização de custo?
            {' '}
            <img src={info} alt="info" title="Irá exibir os bilhetes na carta mesmo que o resultado não tenha tido uma otimização de custo (comparada ao valor inicial)" />
          </div>
        </div>
        <FilterRadioButtonsContainer>
          <FilterRadioButton selected={showTicketsAtNotOptimizedResults} onClick={() => setShowTicketsAtNotOptimizedResults(true)}>
            Sim
          </FilterRadioButton>
          <FilterRadioButton selected={!showTicketsAtNotOptimizedResults} onClick={() => setShowTicketsAtNotOptimizedResults(false)}>
            Não
          </FilterRadioButton>
        </FilterRadioButtonsContainer>
      </Group>

      <Group>
        <div className="title">
          <div>
            Quando um resultado tiver um item sem relacionamento, enviar solicitação para o cliente relacionar?
            {' '}
            <img src={info} alt="info" title="Enviará um e-mail com um link para o cliente acessar e relacionar o item de VT que não existe na envelopadora." />
          </div>
        </div>
        <FilterRadioButtonsContainer>
          <FilterRadioButton selected={canRequestVtItemRelation} onClick={() => setCanRequestVtItemRelation(true)}>
            Sim
          </FilterRadioButton>
          <FilterRadioButton selected={!canRequestVtItemRelation} onClick={() => setCanRequestVtItemRelation(false)}>
            Não
          </FilterRadioButton>
        </FilterRadioButtonsContainer>
      </Group>

      <Group>
        <div className="title">
          <div>
            Utiliza texto personalizado na carta?
            {' '}
            <img src={info} alt="info" title="Caso queira utilizar um texto personalizado na carta de vale transporte, digite o mesmo no campo abaixo" />
          </div>
        </div>
        <FilterRadioButtonsContainer>
          <FilterRadioButton selected={hasCustomTextAtLetter} onClick={() => handleHasCustomTextAtLetterChange(true)}>
            Sim
          </FilterRadioButton>
          <FilterRadioButton selected={!hasCustomTextAtLetter} onClick={() => handleHasCustomTextAtLetterChange(false)}>
            Não
          </FilterRadioButton>
        </FilterRadioButtonsContainer>

        {hasCustomTextAtLetter && (
          <OpacityAnimation>
            <FormGroup error={getErrorMessageByFieldName('customTextAtLetter')}>
              <Textarea
                value={customTextAtLetter}
                onChange={handleCustomTextAtLetterChange}
                placeholder="Escreva aqui o texto..."
                maxLength={800}
              />
            </FormGroup>
          </OpacityAnimation>
        )}

      </Group>
    </Container>
  );
}
